/**
 * Responsible for using the creditcard behavior within a floating label.
 * @module elements/fields/iconifyCreditCard
 */
import $ from 'jquery';
import 'jquery-mask-plugin';
import setup, { VISIBLE_CLASS_NAME, ICON_CLASS_NAME, CONTAINER_CLASS_NAME } from './iconify-setup';
import { TYPES } from './floating-label';

export { VISIBLE_CLASS_NAME };

/**
 * Contains the list of icons associated with credicards
 * @type {Array}
 */
export const iconList = [
  { name: 'visa', iconId: 'cc-visa', iconSize: 'lg', nodeType: 'div' },
  { name: 'amex', iconId: 'cc-amex', iconSize: 'lg', nodeType: 'div' },
  { name: 'master', iconId: 'cc-mc', iconSize: 'lg', nodeType: 'div' },
  { name: 'discover', iconId: 'cc-discover', iconSize: 'lg', nodeType: 'div' },
  { name: 'jcb', iconId: 'cc-jcb', iconSize: 'lg', nodeType: 'div' },
  { name: 'unionpay', iconId: 'cc-unionpay', iconSize: 'lg', nodeType: 'div' },
  { name: 'generic', iconId: 'cc-generic', iconSize: 'lg', nodeType: 'div' },
];

/**
 * Contains the list of regex associated with credicards
 * @type {Array}
 */
export const regExList = [
  { name: 'visa', regex: '^4' },
  { name: 'amex', regex: '^(34|37)' },
  { name: 'master', regex: '^5[1-5]' },
  { name: 'jcb', regex: '^(352[89]|35[3-8][0-9])' },
  { name: 'discover', regex: '^(6011|65|64[4-9]|622(1(2[6-9]|[3-9])|[2-8]{2}|9([01]|2[0-5])))' },
];
/**
 * hides all icons by removing the visibility className.
 * @private
 * @param {HTMLElement} container - The icon container
 */
const hideAll = (container) => {
  Array.from(container.querySelectorAll(`.${ICON_CLASS_NAME}`)).forEach((el) => {
    el.classList.remove(VISIBLE_CLASS_NAME);
  });
};

/**
 * Show Card Icon
 * @private
 * @param {jQuery} $activeCard - active card icon
 * @param {HTMLElement} container - The icon container
 */
const showCard = ($activeCard, container) => {
  hideAll(container);
  $activeCard.addClass(VISIBLE_CLASS_NAME);
};

/**
 * This is an alternative to getCardIcons to show card without mask
 * @param {HTMLFormElement} formField - The form element in the floating label
 * @param {HTMLElement} container - The icon container
 * @param {Object} iconNodes - icons keyed by icon name with icon HTML element
 */
const showCardWithoutMask = ($formField, container, iconNodes, initRegExpList) => {
  const ccNum = $formField.val();
  const [{ name: activeCard } = { name: 'generic' }] = initRegExpList.filter(({ regex }) => regex.test(ccNum));
  const $activeCard = $(iconNodes[activeCard]);
  showCard($activeCard, container);
};
/**
 * Shows the active credit card icon
 * @param {HTMLFormElement} formField - The form element in the floating label
 * @param {HTMLElement} container - The icon container
 * @param {Object} iconNodes - icons keyed by icon name with icon HTML element
 */
const getCardIcon = (
    $formField,
    container,
    iconNodes,
    defaultCCTemplate,
    amexCCTemplate,
    initRegExpList,
    maskOptions,
  ) => {
  const ccNum = $formField.val();
  const [{ name: activeCard } = { name: 'generic' }] = initRegExpList.filter(({ regex }) => regex.test(ccNum));
  const $activeCard = $(iconNodes[activeCard]);
  $formField.mask(activeCard === 'amex' ? amexCCTemplate : defaultCCTemplate, maskOptions);
  showCard($activeCard, container);
};
/**
 * Initialize credit cards' regex
 * @private
 * @returns {Array} - returns initialized regex array
 */
const initRegex = () => regExList.map(({ name, regex }) => ({
  name, regex: new RegExp(regex),
}));

/**
 * Set Data Attributes for each credit card icons
 * @private
 * @param {HTMLFormElement} formField - The form element in the floating label
 * @param {HTMLElement} container - The icon container
 * @param {Object} iconNodes - icons keyed by icon name with icon HTML element
 */
const applyMask = ($formField, container, iconNodes) => {
  const defaultCCTemplate = '0000 0000 0000 0000';
  const amexCCTemplate = '0000 000000 00000';
  const initRegExpList = initRegex();
  const maskOptions = {
    onKeyPress: () => {
      getCardIcon(
        $formField,
        container,
        iconNodes,
        defaultCCTemplate,
        amexCCTemplate,
        initRegExpList,
        maskOptions,
      );
    },
  };
  getCardIcon(
    $formField,
    container,
    iconNodes,
    defaultCCTemplate,
    amexCCTemplate,
    initRegExpList,
    maskOptions,
  );
  $formField.mask(defaultCCTemplate, maskOptions);
};
/**
 * Set Event Listeners
 * @private
 * @param {HTMLFormElement} formField - The form element in the floating label
 * @param {HTMLElement} container - The icon container
 * @param {Object} iconNodes - icons keyed by icon name with icon HTML element
 */
const addEventListeners = ($formField, container, iconNodes, isVue) => {
  const initRegExpList = initRegex();
  showCard($(iconNodes.generic), container);
  $formField.on('keydown.iconifyCreditCard', (e) => {
    if (e.keyCode === 8 || e.which === 8 || e.key === 'Backspace') {
      if ($formField.val().length === 1) {
        showCard($(iconNodes.generic), container);
      }
    }
  });
  if (isVue) {
    $formField.on('input.iconifyCreditCard', () => {
      showCardWithoutMask(
        $formField,
        container,
        iconNodes,
        initRegExpList,
      );
    });
  }
};
/**
 * Set Data Attributes for each credit card icons
 * @param {jQuery} $floatingLabel - floating label element
 */
const setIconDataAttr = ($floatingLabel) => {
  iconList.forEach(({ name }) => {
    $floatingLabel.attr(`data-${CONTAINER_CLASS_NAME}-${name}`, name);
  });
};

/**
 * @param {Object} options - Configurable options
 * @returns {Function} Initialization of iconify credit card
 */
const iconifyCreditCard = (options = {}) => (...nodes) => {
  const opts = $.extend({}, options);
  const [{ node: floatingLabel }] = nodes.filter(({ type }) => TYPES.CONTAINER === type);
  const [{ node: formField }] = nodes.filter(({ type }) => TYPES.FIELD === type);
  const isCreditCardVerify = floatingLabel.dataset.iconifyCreditcard;
  const isVue = $('body').attr('data-vue') === 'true';
  let ccType = $(formField).attr('data-cc-type') || $(floatingLabel).attr('data-cc-type');
  if (isCreditCardVerify) {
    setIconDataAttr($(floatingLabel));
    const { iconContainer, iconNodes } = setup(iconList, nodes, opts);
    addEventListeners($(formField), iconContainer, iconNodes, isVue);
    if (!isVue) {
      applyMask($(formField), iconContainer, iconNodes);
    }
    if (ccType) {
      showCard($(iconNodes[ccType]), iconContainer);
    }
    $(formField).on('showcard', () => {
      ccType = $(formField).attr('data-cc-type') || $(floatingLabel).attr('data-cc-type');
      if (ccType) {
        showCard($(iconNodes[ccType]), iconContainer);
      }
    });
    $(floatingLabel).on('showcard', () => {
      ccType = $(formField).attr('data-cc-type') || $(floatingLabel).attr('data-cc-type');
      if (ccType) {
        showCard($(iconNodes[ccType]), iconContainer);
      }
    });
  }
};

export default iconifyCreditCard;



// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/iconify-creditcard.js